/**
 * Cloudinary delivery URL helpers — single source for f_auto / quality / width.
 * Prefer transforming at render or insert time; DB stores Cloudinary URLs or public_id.
 */

const DEFAULT_QUALITY = 85;
const DEFAULT_WIDTH = 1200;

/** Homepage / catalog cards (~h-52). */
export const CLOUDINARY_CARD_IMAGE_OPTS = {
  width: 480,
  quality: 75,
  format: "auto",
};

/** Tour detail hero / lightbox. */
export const CLOUDINARY_DETAIL_IMAGE_OPTS = {
  width: 1200,
  quality: 85,
  format: "auto",
};

/** Detail side thumbs / related. */
export const CLOUDINARY_THUMB_IMAGE_OPTS = {
  width: 160,
  quality: 70,
  format: "auto",
};

export const CLOUDINARY_BODY_IMAGE_OPTS = {
  width: DEFAULT_WIDTH,
  quality: DEFAULT_QUALITY,
  format: "auto",
};

/**
 * Build a delivery URL from public_id.
 * @param {string} publicId - e.g. blogwriter/posts/abc123
 * @param {{ width?: number, quality?: number, format?: string }} [opts]
 */
export function buildCloudinaryDeliveryUrl(publicId, opts = {}) {
  const id = String(publicId || "").replace(/^\/+/, "").trim();
  if (!id) return "";
  const cloud =
    typeof process !== "undefined" &&
    process.env?.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME
      ? String(process.env.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME).trim()
      : "";
  if (!cloud) return "";
  const w = opts.width ?? DEFAULT_WIDTH;
  const q = opts.quality ?? DEFAULT_QUALITY;
  const f = opts.format || "auto";
  return `https://res.cloudinary.com/${cloud}/image/upload/f_${f},q_${q},w_${w}/${id}`;
}

function isTransformSegment(segment) {
  const head = String(segment || "");
  if (!head) return false;
  if (/^v\d+$/i.test(head)) return false;
  return (
    head.includes(",") ||
    /^(f_|q_|w_|h_|c_|g_|e_|b_|a_|dpr_|fl_|t_|o_|r_)/i.test(head)
  );
}

/**
 * Insert/replace f_auto,q_*,w_* on a Cloudinary upload URL.
 * Leaves non-Cloudinary URLs unchanged. Rewrites existing transforms when opts change.
 */
export function withCloudinaryDeliveryTransforms(url, opts = {}) {
  const raw = String(url || "").trim();
  if (!raw) return raw;
  if (!/res\.cloudinary\.com/i.test(raw)) return raw;

  const w = opts.width ?? DEFAULT_WIDTH;
  const q = opts.quality ?? DEFAULT_QUALITY;
  const f = opts.format || "auto";
  const transform = `f_${f},q_${q},w_${w}`;

  const m = raw.match(
    /^(https?:\/\/res\.cloudinary\.com\/[^/]+\/image\/upload\/)(.+)$/i,
  );
  if (!m) return raw;

  const segments = m[2].split("/").filter(Boolean);
  while (segments.length > 0 && isTransformSegment(segments[0])) {
    segments.shift();
  }
  if (!segments.length) return raw;
  return `${m[1]}${transform}/${segments.join("/")}`;
}

/**
 * Ticket 4 — card vs detail delivery sizes (WebP/AVIF via f_auto).
 * @param {string} url
 * @param {"card"|"detail"|"thumb"} [variant]
 */
export function productImageUrl(url, variant = "detail") {
  const opts =
    variant === "card"
      ? CLOUDINARY_CARD_IMAGE_OPTS
      : variant === "thumb"
        ? CLOUDINARY_THUMB_IMAGE_OPTS
        : CLOUDINARY_DETAIL_IMAGE_OPTS;
  const out = withCloudinaryDeliveryTransforms(url, opts);
  return out || String(url || "").trim();
}

/**
 * Extract Cloudinary public_id from a delivery URL or bare id.
 */
export function extractCloudinaryPublicId(urlOrId) {
  const raw = String(urlOrId || "").trim();
  if (!raw) return "";
  if (!/^https?:\/\//i.test(raw)) {
    return raw.replace(/\.[a-z0-9]+$/i, "").replace(/^\/+/, "");
  }
  if (!/res\.cloudinary\.com/i.test(raw)) return "";
  const m = raw.match(/\/image\/upload\/(.+)$/i);
  if (!m) return "";
  let rest = m[1];
  rest = rest.replace(/^v\d+\//, "");
  const parts = rest.split("/");
  while (
    parts.length > 1 &&
    (/[,_]/.test(parts[0]) ||
      /^(f_|q_|w_|h_|c_|g_|e_|b_|a_|dpr_)/i.test(parts[0]))
  ) {
    parts.shift();
  }
  return parts.join("/").replace(/\.[a-z0-9]+$/i, "");
}

/**
 * Normalize upload API result into a media asset object (future-proof).
 */
export function toMediaAsset(uploadResult, folder = "") {
  const url = String(uploadResult?.url || uploadResult?.secure_url || "").trim();
  const publicId = String(uploadResult?.public_id || "").trim();
  return {
    public_id: publicId || null,
    folder: folder || null,
    width: uploadResult?.width ?? null,
    height: uploadResult?.height ?? null,
    format: uploadResult?.format ?? null,
    url: url ? withCloudinaryDeliveryTransforms(url) : null,
  };
}

export default {
  buildCloudinaryDeliveryUrl,
  withCloudinaryDeliveryTransforms,
  productImageUrl,
  extractCloudinaryPublicId,
  toMediaAsset,
  CLOUDINARY_BODY_IMAGE_OPTS,
  CLOUDINARY_CARD_IMAGE_OPTS,
  CLOUDINARY_DETAIL_IMAGE_OPTS,
  CLOUDINARY_THUMB_IMAGE_OPTS,
};
